import { useCallback, useContext } from "react";
import { API } from "../global/global";
import { AuthContext } from "../context/AuthContext";
import { toast } from "react-toastify";
import JSZip from "jszip";

interface FiltrosDescarga {
  desde?: string;
  hasta?: string;
  etapa?: number;
  includeAll?: boolean;
}

const useDownloadMassivePDFIndividual = (
  loadingFunction: (value: React.SetStateAction<boolean>) => void,
  progressFunction?: (current: number, total: number) => void
) => {
  const authContext = useContext(AuthContext);

  // Función para obtener la lista de servicios
  const obtenerServicios = async (filtros?: FiltrosDescarga) => {
    let url = `/actividad?includeAll=true&ultima_etapa[eq]=${filtros?.etapa || 3}`;
    
    if (filtros?.desde) {
      url += `&fecha[gte]=${filtros.desde}`;
    }
    if (filtros?.hasta) {
      url += `&fecha[lte]=${filtros.hasta}`;
    }

    const response = await fetch(`${API}${url}`, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: "Bearer " + authContext.token,
      },
    });

    if (!response.ok) {
      throw new Error(`Error al obtener servicios: ${response.status}`);
    }

    const data = await response.json();
    return data.data || [];
  };

  // Función para descargar un PDF individual
  const descargarPDFIndividual = async (idActividad: number) => {
    const response = await fetch(`${API}/downloadPDF/${idActividad}`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + authContext.token,
      },
    });

    if (!response.ok) {
      throw new Error(`Error al descargar PDF ${idActividad}: ${response.status}`);
    }

    return await response.blob();
  };

  // Función principal de descarga masiva
  const descargarPDFsMasivo = useCallback(
    async (filtros?: FiltrosDescarga) => {
      loadingFunction(true);
      
      try {
        // Obtener lista de servicios
        toast.info("Obteniendo lista de servicios...", {
          position: "top-right",
          autoClose: 2000,
        });

        const servicios = await obtenerServicios(filtros);
        
        if (servicios.length === 0) {
          toast.warning("No se encontraron servicios que coincidan con los filtros aplicados", {
            position: "top-right",
            autoClose: 5000,
          });
          loadingFunction(false);
          return;
        }

        toast.info(`Descargando ${servicios.length} PDFs...`, {
          position: "top-right",
          autoClose: 3000,
        });

        // Crear ZIP
        const zip = new JSZip();
        let exitosos = 0;
        let fallidos = 0;

        // Descargar PDFs individualmente
        for (let i = 0; i < servicios.length; i++) {
          const servicio = servicios[i];
          
          try {
            if (progressFunction) {
              progressFunction(i + 1, servicios.length);
            }

            const pdfBlob = await descargarPDFIndividual(servicio.id);
            
            // Agregar al ZIP con nombre descriptivo
            const nombreArchivo = `Servicio_${servicio.id}_${servicio.fecha || 'sin_fecha'}.pdf`;
            zip.file(nombreArchivo, pdfBlob);
            exitosos++;
            
          } catch (error) {
            console.error(`Error descargando PDF ${servicio.id}:`, error);
            fallidos++;
          }
        }

        if (exitosos === 0) {
          toast.error("No se pudo descargar ningún PDF", {
            position: "top-right",
            autoClose: 5000,
          });
          loadingFunction(false);
          return;
        }

        // Generar ZIP
        toast.info("Generando archivo ZIP...", {
          position: "top-right",
          autoClose: 2000,
        });

        const zipBlob = await zip.generateAsync({ type: "blob" });
        
        // Descargar ZIP
        const url = window.URL.createObjectURL(zipBlob);
        const anchor = document.createElement("a");
        document.body.appendChild(anchor);
        anchor.href = url;
        
        // Generar nombre del archivo
        const fechaActual = new Date().toISOString().split('T')[0];
        let nombreArchivo = `Servicios_PDFs_${fechaActual}`;
        
        if (filtros?.desde && filtros?.hasta) {
          nombreArchivo = `Servicios_PDFs_${filtros.desde}_${filtros.hasta}`;
        }
        
        anchor.download = `${nombreArchivo}.zip`;
        anchor.click();
        
        // Limpiar recursos
        window.URL.revokeObjectURL(url);
        document.body.removeChild(anchor);
        
        // Mensaje de éxito
        const mensaje = fallidos > 0 
          ? `Descarga completada: ${exitosos} exitosos, ${fallidos} fallidos`
          : `¡Descarga completada exitosamente! ${exitosos} PDFs descargados`;
          
        toast.success(mensaje, {
          position: "top-right",
          autoClose: 5000,
        });
        
      } catch (error) {
        console.error("Error en descarga masiva:", error);
        toast.error("Error al descargar los archivos. Por favor, intente nuevamente.", {
          position: "top-right",
          autoClose: 5000,
        });
      } finally {
        loadingFunction(false);
        if (progressFunction) {
          progressFunction(0, 0); // Reset progress
        }
      }
    },
    [authContext.token, loadingFunction, progressFunction]
  );

  return { descargarPDFsMasivo };
};

export default useDownloadMassivePDFIndividual;
